import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CartLine, CartTotals, CreditSummary } from '../../core/models';
import { selectAllCatalogProducts } from '../catalog/catalog.selectors';
import { selectCreditInfo } from '../profile/profile.selectors';
import { CartState } from './cart.reducer';

export const selectCartState = createFeatureSelector<CartState>('cart');

export const selectCartItems = createSelector(selectCartState, (s) => s.items);

/** Resolve the cart map into product lines (only products still known). */
export const selectCartLines = createSelector(
  selectCartItems,
  selectAllCatalogProducts,
  (items, products): CartLine[] =>
    Object.keys(items)
      .map((id) => {
        const product = products.find((p) => p.id === id);
        if (!product) return null;
        const qty = items[id];
        return { product, qty, line: product.price * qty };
      })
      .filter((l): l is CartLine => l !== null),
);

export const selectCartCount = createSelector(selectCartLines, (lines) =>
  lines.reduce((a, l) => a + l.qty, 0),
);

export const selectCartTotals = createSelector(
  selectCartLines,
  (lines): CartTotals => {
    const subtotal = lines.reduce((a, l) => a + l.line, 0);
    return {
      count: lines.reduce((a, l) => a + l.qty, 0),
      skuCount: lines.length,
      subtotal,
      total: subtotal,
    };
  },
);

export const selectCreditSummary = createSelector(
  selectCreditInfo,
  selectCartTotals,
  (credit, totals): CreditSummary => {
    const isFranchise = credit?.isFranchise ?? false;
    const limit = credit?.creditLimit ?? 0;
    const used = credit?.usedCreditLimit ?? 0;
    const available = credit?.availableCredit ?? Math.max(0, limit - used);
    const afterRaw = available - totals.total;
    return {
      isFranchise,
      limit,
      used,
      available,
      usedPct: limit ? Math.round((used / limit) * 100) : 0,
      afterOrder: Math.max(0, afterRaw),
      overBy: isFranchise && afterRaw < 0 ? -afterRaw : 0,
      isOver: isFranchise && afterRaw < 0,
    };
  },
);

/** Quantity currently in the cart for a given product id. */
export const selectQtyFor = (id: string) =>
  createSelector(selectCartItems, (items) => items[id] ?? 0);
